import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const ChangePassword = () => {
  const navigate = useNavigate();
  const { user, setUser, updateUser } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Récupérer l'utilisateur (contexte ou localStorage)
  let currentUser = user;
  if (!currentUser) {
    try {
      const stored = localStorage.getItem('user');
      if (stored) currentUser = JSON.parse(stored);
    } catch (e) {
      currentUser = null;
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }

    if (newPassword.length < 6) {
      setError('Le nouveau mot de passe doit contenir au moins 6 caractères.');
      return;
    }

    if (currentPassword === newPassword) {
      setError('Le nouveau mot de passe doit être différent de l\'ancien.');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');

      const response = await fetch('/api/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          new_password_confirmation: confirmPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Erreur lors du changement');
      }

      // 🔑 1. Mettre à jour l'utilisateur dans localStorage
      const updatedUser = { ...currentUser, must_change_password: false };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // 🔑 2. Mettre à jour le contexte React (essayer setUser puis updateUser)
      if (setUser) {
        setUser(updatedUser);
      } else if (updateUser) {
        updateUser(updatedUser);
      }

      setSuccess('Mot de passe changé avec succès ! Redirection...');

      // 🔑 3. Redirection après délai pour laisser React mettre à jour
      setTimeout(() => {
        const role = updatedUser.role;

        if (role === 'super_admin') navigate('/super-admin', { replace: true });
        else if (role === 'admin') navigate('/admin', { replace: true });
        else if (role === 'teacher' || role === 'principal_teacher') navigate('/teacher', { replace: true });
        else if (role === 'student') navigate('/student', { replace: true });
        else if (role === 'parent') navigate('/parent', { replace: true });
        else navigate('/', { replace: true });

        // 🔑 4. Forcer un rafraîchissement complet pour être sûr
        window.location.reload();
      }, 800);

    } catch (err) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] p-6">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl p-10">

        <div className="flex flex-col items-center mb-8">
          <div className="bg-[#0A0A0A] rounded-2xl px-6 py-4 mb-4">
            <img src="/images/logo.png" alt="SamaEcole" className="h-14 w-auto object-contain" />
          </div>
        </div>

        <div className="mb-8 text-center">
          <h2 className="text-2xl font-extrabold text-gray-900">
            Changement de mot de passe obligatoire
          </h2>
          <p className="text-gray-500 text-sm mt-2">
            Pour votre sécurité, vous devez changer votre mot de passe avant de continuer.
          </p>
        </div>

        <div className="bg-[#F39C12]/10 border border-[#F39C12]/30 text-[#D35400] text-sm rounded-lg px-4 py-3 mb-6">
          💡 Votre compte a été créé avec un mot de passe temporaire. Veuillez en choisir un nouveau.
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-6">
            ✅ {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Mot de passe actuel
            </label>
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                placeholder="Mot de passe actuel"
                className="w-full bg-white border border-gray-300 rounded-xl pl-4 pr-12 py-3 text-gray-900 focus:outline-none focus:border-[#F39C12] focus:ring-2 focus:ring-[#F39C12]/20"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute top-1/2 -translate-y-1/2 right-4 text-gray-400 hover:text-[#F39C12]"
              >
                👁️
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Nouveau mot de passe
            </label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Au moins 6 caractères"
                className="w-full bg-white border border-gray-300 rounded-xl pl-4 pr-12 py-3 text-gray-900 focus:outline-none focus:border-[#F39C12] focus:ring-2 focus:ring-[#F39C12]/20"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute top-1/2 -translate-y-1/2 right-4 text-gray-400 hover:text-[#F39C12]"
              >
                👁️
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Confirmer le nouveau mot de passe
            </label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Répétez le nouveau mot de passe"
                className="w-full bg-white border border-gray-300 rounded-xl pl-4 pr-12 py-3 text-gray-900 focus:outline-none focus:border-[#F39C12] focus:ring-2 focus:ring-[#F39C12]/20"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute top-1/2 -translate-y-1/2 right-4 text-gray-400 hover:text-[#F39C12]"
              >
                👁️
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-orange w-full text-white font-semibold py-4 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? 'Enregistrement...' : 'Changer mon mot de passe'}
          </button>
        </form>

        <button
          onClick={() => {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            navigate('/login');
          }}
          className="w-full mt-4 text-center text-gray-500 hover:text-[#F39C12] text-sm transition"
        >
          Se déconnecter
        </button>
      </div>
    </div>
  );
};

export default ChangePassword;